import { useMemo, useState } from "react";
import { CATEGORY_META, HOT_EVENTS } from "@/mocks/gossip";
import SectionHeading from "./SectionHeading";

const FILTERS = [
  { id: "all", name: "全部" },
  { id: "film", name: "影视" },
  { id: "variety", name: "综艺" },
  { id: "music", name: "音乐" },
  { id: "redcarpet", name: "红毯" },
  { id: "brand", name: "品牌" },
  { id: "tech", name: "科技" },
];

export default function HotEvents() {
  const [cat, setCat] = useState("all");
  const [keyword, setKeyword] = useState("");
  const [followed, setFollowed] = useState<Set<string>>(new Set());

  const list = useMemo(() => {
    return HOT_EVENTS.filter((e) => {
      const okCat = cat === "all" || e.category === cat;
      const kw = keyword.trim().toLowerCase();
      const okKw = !kw || e.title.toLowerCase().includes(kw) || e.excerpt.toLowerCase().includes(kw);
      return okCat && okKw;
    });
  }, [cat, keyword]);

  const toggleFollow = (id: string) => {
    setFollowed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <section id="hot" className="w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SectionHeading
            eyebrow="今日热点 · 新瓜速递"
            title="在追的热点，一条别错过"
            desc="按发生顺序实时排好的最新吃瓜事件，每条都标注了推进状态。想按分类蹲点，切换标签即可过滤。"
          />
          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索事件关键词…"
              className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const active = cat === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setCat(f.id)}
                className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                }`}
              >
                {f.name}
              </button>
            );
          })}
          <span className="ml-auto hidden text-sm text-foreground-400 sm:inline">
            共 {list.length} 条热点
          </span>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {list.map((e) => {
            const meta = CATEGORY_META[e.category];
            const isFollow = followed.has(e.id);
            return (
              <article
                key={e.id}
                className="group flex gap-4 rounded-2xl border border-background-200 bg-background-100 p-4 transition-colors hover:border-background-300 md:gap-5 md:p-5"
              >
                <div className="flex flex-col items-center pt-1">
                  <span
                    className={`font-heading text-3xl font-black leading-none ${
                      e.rank <= 3 ? "text-accent-500" : "text-background-400"
                    }`}
                  >
                    {String(e.rank).padStart(2, "0")}
                  </span>
                  <span className="mt-2 hidden w-px flex-1 bg-background-200 sm:block" />
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-foreground-700">
                      <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                      {meta.name}
                    </span>
                    <span className="whitespace-nowrap rounded-md bg-accent-100 px-2 py-0.5 text-xs font-bold text-accent-800">
                      {e.tag}
                    </span>
                    <span className="text-xs text-foreground-400">
                      <i className="ri-time-line mr-1" />
                      {e.time}
                    </span>
                    <span className="ml-auto flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-500">
                      <i className="ri-fire-fill text-accent-500" />
                      {e.heat} 热度
                    </span>
                  </div>

                  <h3 className="mt-2 font-heading text-lg font-bold leading-snug text-foreground-950 md:text-xl">
                    {e.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{e.excerpt}</p>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <a
                      href="#collections"
                      className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-bold text-primary-600 transition-colors hover:text-primary-700"
                    >
                      进入合集时间线
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
                    </a>
                    <button
                      type="button"
                      onClick={() => toggleFollow(e.id)}
                      className={`ml-auto inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${
                        isFollow
                          ? "border-primary-300 bg-primary-100 text-primary-700"
                          : "border-background-200 text-foreground-600 hover:border-primary-200 hover:text-primary-600"
                      }`}
                    >
                      <i className={isFollow ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                      {isFollow ? "已追更" : "追更此瓜"}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {list.length === 0 && (
          <div className="mt-6 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <i className="ri-inbox-2-line text-4xl text-background-400" />
            <p className="mt-4 text-sm text-foreground-500">没有搜到相关热点，换个关键词或分类试试吧。</p>
          </div>
        )}
      </div>
    </section>
  );
}